/**
 * Operations — transport pricing, the vehicle fleet, the Contractor roster,
 * and the Supplier directory.
 *
 * The four are destinations, not one long scroll: the landing lists them as
 * drill-down rows and opens one at a time, the Documents & emails pattern.
 * Each is self-fetching, so a space is just its own component behind a
 * BackLink. Transport is setting rows that commit themselves (numbers on
 * blur, the mode select on change); the three rosters are list rows with
 * inline add/edit.
 */
import { useEffect, useState } from "react";
import { AddressAutocomplete } from "../../components/ui/AddressAutocomplete";
import { Banner } from "../../components/ui/Banner";
import { Button } from "../../components/ui/Button";
import { ConfirmModal } from "../../components/ui/ConfirmModal";
import { ContractorModal } from "../../components/admin/ContractorModal";
import { PencilIcon, TrashIcon } from "../../components/ui/icons";
import {
  SelectSettingRow,
  SettingRow,
  ToggleSettingRow,
} from "../../components/ui/SettingRow";
import forms from "../../components/ui/field.module.css";
import { cx } from "@shared/cx";
import {
  getCompanySettings,
  saveCompanySettings,
  type CompanySettings,
} from "../../lib/company-settings-api";
import {
  createVehicle,
  deleteVehicle,
  getTransportConfig,
  listVehicles,
  saveTransportConfig,
  updateVehicle,
  type BeyondZoneMode,
  type CompanyVehicle,
  type TransportConfig,
  type VehicleCreate,
} from "../../lib/transport-api";
import {
  deleteContractor,
  listContractors,
  type Contractor,
} from "../../lib/contractors-api";
import {
  createWarehouse,
  listWarehouses,
  retireWarehouse,
  updateWarehouse,
  type CompanyWarehouse,
  type WarehouseCreate,
} from "../../lib/warehouses-api";
import {
  BackLink,
  DrillDownRow,
  Loading,
  numOrNull,
  SectionHeader,
} from "./section-chrome";
import { SuppliersSection } from "./SuppliersSection";
import chrome from "./settings.module.css";
import styles from "./OperationsSection.module.css";

const BACK_LABEL = "All operations";

type OperationsView =
  | "transport"
  | "warehouses"
  | "vehicles"
  | "contractors"
  | "suppliers"
  | null;

export function OperationsSection() {
  const [view, setView] = useState<OperationsView>(null);
  const back = () => setView(null);

  if (view === "transport") return <TransportGroup onBack={back} />;
  if (view === "warehouses") return <WarehousesGroup onBack={back} />;
  if (view === "vehicles") return <VehiclesGroup onBack={back} />;
  if (view === "contractors") return <ContractorsSection onBack={back} />;
  if (view === "suppliers") return <SuppliersSection onBack={back} />;

  return (
    <section className={cx(chrome.section, chrome.sectionGroups)}>
      <SectionHeader
        title="Operations"
        description="How you get gear to a job, and who you work with. Open one to change it."
      />
      {/* Stock is a setting, not a destination, so it cannot join the
          drill-down list below — it sits above it as a bare row. No group
          heading: one row under a heading that only restates the row. */}
      <StockGroup />
      {/* Destinations, no group headings: one flat list is shorter to
          read than headings over one row each. */}
      <ul className={chrome.listBordered}>
        <DrillDownRow
          name="Transport"
          description="Where delivery is measured from, and what it costs."
          onOpen={() => setView("transport")}
        />
        <DrillDownRow
          name="Warehouses"
          description="Your sites, and which one holds what."
          onOpen={() => setView("warehouses")}
        />
        <DrillDownRow
          name="Vehicles"
          description="The fleet available when planning booking transport."
          onOpen={() => setView("vehicles")}
        />
        <DrillDownRow
          name="Contractors"
          description="The crew roster you assign work to."
          onOpen={() => setView("contractors")}
        />
        <DrillDownRow
          name="Suppliers"
          description="Businesses you hire equipment in from."
          onOpen={() => setView("suppliers")}
        />
      </ul>
    </section>
  );
}

/* ─── Stock ─────────────────────────────────────────────────────────────── */

/**
 * One toggle: whether a quote holds the gear it lists (#314). Off means gear
 * is only reserved from approval onward, so several quotes can offer the same
 * item — the first approval takes it and the rest get a re-pick banner.
 * Approved onward always reserves and is not configurable.
 */
function StockGroup() {
  const [settings, setSettings] = useState<CompanySettings | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    getCompanySettings(ac.signal)
      .then(setSettings)
      .catch((err) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(err instanceof Error ? err.message : String(err));
      });
    return () => ac.abort();
  }, []);

  if (error) {
    return (
      <p role="alert" className={chrome.errorText}>
        {error}
      </p>
    );
  }
  if (!settings) return <Loading />;

  return (
    <ToggleSettingRow
      id="quotes-hold-stock"
      title="Quotes hold stock"
      description="Reserve equipment as soon as it is quoted, not only once the job is approved."
      tooltip="Approved bookings always hold their gear; this only decides whether quotes do too."
      checked={settings.quotes_hold_stock}
      onChange={async (next) => {
        const saved = await saveCompanySettings({ quotes_hold_stock: next });
        setSettings(saved);
      }}
    />
  );
}

/**
 * One drilled-into Operations space: back link, then the space's own heading.
 * The heading is an H2 (SectionHeader), not a group H3 — a space is a section
 * in its own right once it is the only thing on screen.
 */
function Space({
  title,
  description,
  action,
  onBack,
  children,
}: {
  title: string;
  description: string;
  action?: React.ReactNode;
  /** Absent only where a space is rendered on its own, outside the gallery. */
  onBack?: () => void;
  children: React.ReactNode;
}) {
  return (
    <section className={chrome.section}>
      {onBack && <BackLink label={BACK_LABEL} onClick={onBack} />}
      <SectionHeader title={title} description={description} action={action} />
      {children}
    </section>
  );
}

/* ─── Transport pricing ─────────────────────────────────────────────────── */

const transportEditableKeys = [
  "warehouse_address",
  "base_zone_radius_km",
  "base_zone_rate",
  "beyond_zone_mode",
  "extended_zone_rate",
  "per_km_rate",
] as const;

function TransportGroup({ onBack }: { onBack: () => void }) {
  const [config, setConfig] = useState<TransportConfig | null>(null);
  const [baseline, setBaseline] = useState<TransportConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [geocodeWarning, setGeocodeWarning] = useState(false);

  useEffect(() => {
    const ac = new AbortController();
    getTransportConfig(ac.signal)
      .then((res) => {
        setConfig(res.config);
        setBaseline(res.config);
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(err instanceof Error ? err.message : String(err));
      });
    return () => ac.abort();
  }, []);

  const space = (children: React.ReactNode) => (
    <Space
      title="Transport"
      description="Where delivery is measured from, and what it costs."
      onBack={onBack}
    >
      {children}
    </Space>
  );

  if (!config) return space(<Loading />);

  const set = <K extends keyof TransportConfig>(
    key: K,
    value: TransportConfig[K],
  ) => setConfig({ ...config, [key]: value });

  /** Commit the CURRENT config; every control calls this itself. */
  const save = async (next: TransportConfig) => {
    setSaving(true);
    try {
      const res = await saveTransportConfig({
        warehouse_address: next.warehouse_address,
        base_zone_radius_km: next.base_zone_radius_km,
        base_zone_rate: next.base_zone_rate,
        beyond_zone_mode: next.beyond_zone_mode,
        extended_zone_rate: next.extended_zone_rate,
        per_km_rate: next.per_km_rate,
      });
      setConfig(res.config);
      setBaseline(res.config);
      setGeocodeWarning(!res.geocoded && next.warehouse_address !== "");
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  /** Blur commit for one field, skipped while unchanged. */
  const commit = (key: (typeof transportEditableKeys)[number]) => {
    if (baseline && config[key] !== baseline[key]) void save(config);
  };

  const numRow = (
    id: string,
    title: string,
    key: {
      [K in keyof TransportConfig]: TransportConfig[K] extends number
        ? K
        : never;
    }[keyof TransportConfig],
    options: {
      description?: string;
      tooltip?: string;
      indent?: boolean;
    } = {},
  ) => (
    <SettingRow
      title={title}
      description={options.description}
      tooltip={options.tooltip}
      htmlFor={id}
      indent={options.indent}
    >
      <input
        id={id}
        type="number"
        min={0}
        step="0.01"
        className={cx(forms.input, chrome.shortField)}
        value={String(config[key])}
        disabled={saving}
        onChange={(e) => set(key, numOrNull(e.target.value) ?? 0)}
        onBlur={() => commit(key)}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        }}
      />
    </SettingRow>
  );

  return space(
    <>
      {error && (
        <p role="alert" className={chrome.sectionError}>
          {error}
        </p>
      )}

      <SettingRow
        title="Warehouse address"
        description="Fallback delivery origin when a job's gear isn't tracked to a warehouse — otherwise each warehouse prices its own route."
        htmlFor="warehouse-address"
      >
        <div
          className={styles.addressControl}
          onBlur={(e) => {
            if (e.currentTarget.contains(e.relatedTarget)) return;
            commit("warehouse_address");
          }}
        >
          <AddressAutocomplete
            id="warehouse-address"
            ariaLabel="Warehouse address"
            value={config.warehouse_address}
            onChange={(next) => set("warehouse_address", next)}
          />
        </div>
      </SettingRow>
      {/* Resolved by action (no X): clears when a saved address geocodes. */}
      {geocodeWarning && (
        <Banner variant="warning" className={chrome.bannerBelowTight}>
          Address saved but could not be located on the map — distance
          calculation will need a manually entered distance.
        </Banner>
      )}

      {numRow(
        "base-zone-radius",
        "Base zone radius (km)",
        "base_zone_radius_km",
        {
          tooltip: "Distance from the warehouse covered by the base rate.",
        },
      )}
      {numRow("base-zone-rate", "Base zone rate", "base_zone_rate", {
        description: "$ per vehicle, per leg.",
      })}

      <SelectSettingRow
        id="beyond-zone-mode"
        title="Beyond the base zone"
        description="How delivery is priced past the base zone."
        value={config.beyond_zone_mode}
        options={[
          { value: "flat", label: "Flat rate" },
          { value: "per_km", label: "Per km" },
        ]}
        onChange={(mode) => {
          const next = { ...config, beyond_zone_mode: mode as BeyondZoneMode };
          setConfig(next);
          return save(next);
        }}
      />

      {config.beyond_zone_mode === "flat"
        ? numRow(
            "extended-zone-rate",
            "Extended zone rate",
            "extended_zone_rate",
            {
              description: "$ per vehicle, per leg.",
              indent: true,
            },
          )
        : numRow("per-km-rate", "Per km rate", "per_km_rate", {
            description: "$ / km.",
            tooltip:
              "Applied to km beyond the base zone, on top of the base rate.",
            indent: true,
          })}
    </>,
  );
}

/* ─── Warehouses ────────────────────────────────────────────────────────── */

const emptyWarehouse: WarehouseCreate = { name: "", address: "" };

/**
 * The Company's sites (#304). Modelled on Vehicles, because a warehouse and a
 * truck are the same kind of tenant object — list rows with an inline
 * add/edit panel below the list, an explicit submit (committing a new entity
 * is not saving a setting), and a soft retire.
 *
 * Every Company has exactly one after migration 128, so a single-site Company
 * sees this list with its own warehouse in it and no change anywhere else.
 * Adding the second is what turns the per-warehouse UI on.
 *
 * Retire, never delete: stock rows and booking lines point at a warehouse, so
 * removing one would strand them. The server refuses the last active one.
 */
function WarehousesGroup({ onBack }: { onBack?: () => void }) {
  const [warehouses, setWarehouses] = useState<CompanyWarehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [draft, setDraft] = useState<WarehouseCreate>(emptyWarehouse);
  const [saving, setSaving] = useState(false);
  const [geocodeWarning, setGeocodeWarning] = useState(false);
  const [retireTarget, setRetireTarget] = useState<CompanyWarehouse | null>(
    null,
  );
  const [retiring, setRetiring] = useState(false);
  const [retireError, setRetireError] = useState<string | null>(null);

  const refresh = async (signal?: AbortSignal) => {
    try {
      const list = await listWarehouses(signal);
      if (signal?.aborted) return;
      setWarehouses(list);
      setError(null);
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  useEffect(() => {
    const ac = new AbortController();
    void refresh(ac.signal);
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditId(null);
    setDraft(emptyWarehouse);
    setFormOpen(true);
  };

  const openEdit = (w: CompanyWarehouse) => {
    setEditId(w.id);
    setDraft({ name: w.name, address: w.address });
    setFormOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = editId
        ? await updateWarehouse({ id: editId, ...draft })
        : await createWarehouse(draft);
      setGeocodeWarning(!res.geocoded && draft.address.trim() !== "");
      setFormOpen(false);
      await refresh();
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const retire = async () => {
    if (!retireTarget) return;
    setRetiring(true);
    try {
      await retireWarehouse(retireTarget.id);
      setRetireTarget(null);
      setRetireError(null);
      await refresh();
      setError(null);
    } catch (err) {
      // Stays in the modal beside the warehouse it belongs to — the refusal
      // names why (the last active one cannot go).
      setRetireError(err instanceof Error ? err.message : String(err));
    } finally {
      setRetiring(false);
    }
  };

  const active = warehouses.filter((w) => w.active);

  return (
    <Space
      title="Warehouses"
      description="Your sites, and which one holds what."
      onBack={onBack}
      action={
        <Button size="sm" variant="secondary" onClick={openCreate}>
          Add warehouse
        </Button>
      }
    >
      {error && (
        <p role="alert" className={chrome.sectionError}>
          {error}
        </p>
      )}

      {loading ? (
        <Loading />
      ) : (
        <ul className={chrome.list}>
          {active.map((w) => (
            <li
              key={w.id}
              className={chrome.row}
            >
              <div className={chrome.rowText}>
                <p className={chrome.rowName}>{w.name}</p>
                <p className={chrome.rowMeta}>{w.address || "—"}</p>
              </div>
              <div className={chrome.rowActions}>
                <button
                  type="button"
                  aria-label={`Edit ${w.name}`}
                  className={chrome.iconButton}
                  onClick={() => openEdit(w)}
                >
                  <PencilIcon className={chrome.icon} />
                </button>
                <button
                  type="button"
                  aria-label={`Retire ${w.name}`}
                  className={chrome.iconButton}
                  onClick={() => {
                    setRetireError(null);
                    setRetireTarget(w);
                  }}
                >
                  <TrashIcon className={chrome.icon} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Resolved by action (no X): clears when a saved address geocodes. */}
      {geocodeWarning && (
        <Banner variant="warning" className={chrome.bannerBelow}>
          Address saved but could not be located on the map — delivery from this
          warehouse will need a manually entered distance.
        </Banner>
      )}

      {/* The form sits under the list: a create appends a row there, and the
          panel stays in one place whichever action opened it. */}
      {formOpen && (
        <div className={chrome.panel}>
          <div className={cx(chrome.grid, chrome.gridTwo)}>
            <div>
              <label className={forms.label} htmlFor="warehouse-name">
                Name
              </label>
              <input
                id="warehouse-name"
                className={forms.input}
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </div>
            <div>
              <label className={forms.label} htmlFor="warehouse-site-address">
                Address
              </label>
              <AddressAutocomplete
                id="warehouse-site-address"
                ariaLabel="Warehouse address"
                value={draft.address}
                onChange={(next) => setDraft({ ...draft, address: next })}
              />
            </div>
          </div>
          <div className={chrome.panelActions}>
            <Button
              size="sm"
              loading={saving}
              disabled={draft.name.trim() === ""}
              onClick={() => void save()}
            >
              {editId ? "Save warehouse" : "Add warehouse"}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setFormOpen(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      <ConfirmModal
        open={retireTarget !== null}
        title="Retire warehouse"
        message={`Retire "${retireTarget?.name ?? ""}"? Its stock and past bookings stay on record, but it disappears from pickers.`}
        confirmLabel="Retire"
        loading={retiring}
        error={retireError}
        onConfirm={() => void retire()}
        onCancel={() => {
          setRetireTarget(null);
          setRetireError(null);
        }}
      />
    </Space>
  );
}

/* ─── Vehicles ──────────────────────────────────────────────────────────── */

const emptyVehicle: VehicleCreate = {
  name: "",
  vehicle_type: "",
  capacity_weight_kg: null,
  capacity_volume_m3: null,
};

function VehiclesGroup({ onBack }: { onBack?: () => void }) {
  const [vehicles, setVehicles] = useState<CompanyVehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [draft, setDraft] = useState<VehicleCreate>(emptyVehicle);
  const [saving, setSaving] = useState(false);
  const [retireTarget, setRetireTarget] = useState<CompanyVehicle | null>(null);
  const [retiring, setRetiring] = useState(false);

  const refresh = async (signal?: AbortSignal) => {
    try {
      const list = await listVehicles(signal);
      if (signal?.aborted) return;
      setVehicles(list);
      setError(null);
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  useEffect(() => {
    const ac = new AbortController();
    void refresh(ac.signal);
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditId(null);
    setDraft(emptyVehicle);
    setFormOpen(true);
  };

  const openEdit = (v: CompanyVehicle) => {
    setEditId(v.id);
    setDraft({
      name: v.name,
      vehicle_type: v.vehicle_type,
      capacity_weight_kg: v.capacity_weight_kg,
      capacity_volume_m3: v.capacity_volume_m3,
    });
    setFormOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editId) {
        await updateVehicle({ id: editId, ...draft });
      } else {
        await createVehicle(draft);
      }
      setFormOpen(false);
      await refresh();
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const retire = async () => {
    if (!retireTarget) return;
    setRetiring(true);
    try {
      await deleteVehicle(retireTarget.id);
      setRetireTarget(null);
      await refresh();
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRetiring(false);
    }
  };

  const active = vehicles.filter((v) => v.active);

  return (
    <Space
      title="Vehicles"
      description="The fleet available when planning booking transport."
      onBack={onBack}
      action={
        <Button size="sm" variant="secondary" onClick={openCreate}>
          Add vehicle
        </Button>
      }
    >
      {error && (
        <p role="alert" className={chrome.sectionError}>
          {error}
        </p>
      )}

      {loading ? (
        <Loading />
      ) : active.length === 0 ? (
        <p className={chrome.empty}>
          No vehicles yet. Add trucks and vans so bookings can plan transport.
        </p>
      ) : (
        <ul className={chrome.list}>
          {active.map((v) => (
            <li
              key={v.id}
              className={chrome.row}
            >
              <div className={chrome.rowText}>
                <p className={chrome.rowName}>{v.name}</p>
                <p className={chrome.rowMeta}>
                  {[
                    v.vehicle_type || null,
                    v.capacity_weight_kg != null
                      ? `${v.capacity_weight_kg} kg`
                      : null,
                    v.capacity_volume_m3 != null
                      ? `${v.capacity_volume_m3} m³`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ") || "—"}
                </p>
              </div>
              <div className={chrome.rowActions}>
                <button
                  type="button"
                  aria-label={`Edit ${v.name}`}
                  className={chrome.iconButton}
                  onClick={() => openEdit(v)}
                >
                  <PencilIcon className={chrome.icon} />
                </button>
                <button
                  type="button"
                  aria-label={`Retire ${v.name}`}
                  className={chrome.iconButton}
                  onClick={() => setRetireTarget(v)}
                >
                  <TrashIcon className={chrome.icon} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* The form sits under the list: a create appends a row there, and
          the panel stays in one place whichever action opened it. */}
      {formOpen && (
        <div className={chrome.panel}>
          <div className={cx(chrome.grid, chrome.gridTwo)}>
            <div>
              <label className={forms.label} htmlFor="vehicle-name">
                Name
              </label>
              <input
                id="vehicle-name"
                className={forms.input}
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </div>
            <div>
              <label className={forms.label} htmlFor="vehicle-type">
                Type{" "}
                <span className={forms.optional}>(optional)</span>
              </label>
              <input
                id="vehicle-type"
                className={forms.input}
                value={draft.vehicle_type}
                onChange={(e) =>
                  setDraft({ ...draft, vehicle_type: e.target.value })
                }
              />
            </div>
            <div>
              <label className={forms.label} htmlFor="vehicle-weight">
                Capacity (kg){" "}
                <span className={forms.optional}>(optional)</span>
              </label>
              <input
                id="vehicle-weight"
                type="number"
                min={0}
                className={forms.input}
                value={draft.capacity_weight_kg ?? ""}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    capacity_weight_kg: numOrNull(e.target.value),
                  })
                }
              />
            </div>
            <div>
              <label className={forms.label} htmlFor="vehicle-volume">
                Capacity (m³){" "}
                <span className={forms.optional}>(optional)</span>
              </label>
              <input
                id="vehicle-volume"
                type="number"
                min={0}
                className={forms.input}
                value={draft.capacity_volume_m3 ?? ""}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    capacity_volume_m3: numOrNull(e.target.value),
                  })
                }
              />
            </div>
          </div>
          <div className={chrome.panelActions}>
            <Button
              size="sm"
              loading={saving}
              disabled={draft.name.trim() === ""}
              onClick={() => void save()}
            >
              {editId ? "Save vehicle" : "Add vehicle"}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setFormOpen(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      <ConfirmModal
        open={retireTarget !== null}
        title="Retire vehicle"
        message={`Retire "${retireTarget?.name ?? ""}"? It stays on past bookings but disappears from pickers.`}
        confirmLabel="Retire"
        loading={retiring}
        onConfirm={() => void retire()}
        onCancel={() => setRetireTarget(null)}
      />
    </Space>
  );
}

/* ─── Contractors ───────────────────────────────────────────────────────── */

export function ContractorsSection({ onBack }: { onBack?: () => void } = {}) {
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Contractor | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<Contractor | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [sectionError, setSectionError] = useState<string | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    listContractors(ac.signal)
      .then((rows) => {
        setContractors(rows);
        setSectionError(null);
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setSectionError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });
    return () => ac.abort();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const remove = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      await deleteContractor(removeTarget.id);
      setContractors((rows) =>
        rows.filter((row) => row.id !== removeTarget.id),
      );
      setRemoveTarget(null);
      setSectionError(null);
      setRemoveError(null);
    } catch (err) {
      setRemoveError(err instanceof Error ? err.message : String(err));
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Space
      title="Contractors"
      description="The crew roster available when assigning a Booking."
      onBack={onBack}
      action={
        <Button size="sm" variant="secondary" onClick={openCreate}>
          Add contractor
        </Button>
      }
    >
      {sectionError && (
        <p role="alert" className={chrome.sectionError}>
          {sectionError}
        </p>
      )}

      {loading ? (
        <Loading />
      ) : contractors.length === 0 ? (
        <p className={chrome.empty}>No Contractors yet.</p>
      ) : (
        <ul className={chrome.list}>
          {contractors.map((contractor) => (
            <li
              key={contractor.id}
              className={cx(chrome.row, chrome.rowTall)}
            >
              <div className={chrome.rowText}>
                <p className={chrome.rowName}>
                  {contractor.name}
                </p>
                <p className={cx(chrome.rowMeta, chrome.truncate)}>
                  {[contractor.email, contractor.phone]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
                {contractor.skills.length > 0 && (
                  <p className={chrome.rowNote}>
                    {contractor.skills.join(", ")}
                  </p>
                )}
              </div>
              <div className={chrome.rowActions}>
                <button
                  type="button"
                  aria-label={`Edit ${contractor.name}`}
                  onClick={() => {
                    setEditing(contractor);
                    setModalOpen(true);
                  }}
                  className={chrome.iconButton}
                >
                  <PencilIcon className={chrome.icon} />
                </button>
                <button
                  type="button"
                  aria-label={`Remove ${contractor.name}`}
                  onClick={() => {
                    setRemoveError(null);
                    setRemoveTarget(contractor);
                  }}
                  className={chrome.iconButton}
                >
                  <TrashIcon className={chrome.icon} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <ContractorModal
        isOpen={modalOpen}
        contractor={editing}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSaved={(saved) => {
          setContractors((rows) => {
            const exists = rows.some((row) => row.id === saved.id);
            const next = exists
              ? rows.map((row) => (row.id === saved.id ? saved : row))
              : [...rows, saved];
            return next.sort((a, b) => a.name.localeCompare(b.name));
          });
          setSectionError(null);
        }}
      />

      <ConfirmModal
        open={removeTarget != null}
        title="Remove Contractor"
        message={
          <>
            Remove <strong>{removeTarget?.name ?? ""}</strong> from the Company
            roster?
          </>
        }
        warning="Past Booking Assignments keep their record. Add the same email again to restore this Contractor later."
        error={removeError}
        confirmLabel="Remove"
        confirmVariant="danger"
        loading={removing}
        onConfirm={() => void remove()}
        onCancel={() => {
          setRemoveTarget(null);
          setRemoveError(null);
        }}
      />
    </Space>
  );
}
